import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Button,
  Collapse,
  Divider,
  Link as MaterialLink,
  List,
  ListItemText,
  Menu,
  MenuItem,
  MenuProps,
  alpha,
  styled,
  Stack,
  Box,
  ListItemButton,
} from "@mui/material";
import React from "react";
import { ReactNode, useState } from "react";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link, LinkProps } from "react-router-dom";

export interface DropDownMenuOption {
  title: string;
  path: string;
  icon?: ReactNode | undefined;
  followedByDivider?: boolean;
  disabled?: boolean;
}

export interface DropDownMenuButtonProps {
  id: string;
  title: string;
  options: DropDownMenuOption[];
  disabled?: boolean;
  mobile?: boolean;
  onClick?: () => void;
}
const StyledMenu = styled((props: MenuProps) => (
  <Menu
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    marginTop: theme.spacing(1),
    minWidth: 180,
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const StyledLink = styled((props: LinkProps) => <Link {...props}></Link>)(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.text.primary,
  "&:focus, &:visited, &:hover, &:link, &:active": {
    textDecoration: "none",
  },
}));

export default function DropDownMenuButton({
  id,
  title,
  options,
  disabled = false,
  mobile = false,
  onClick = undefined,
}: DropDownMenuButtonProps) {
  if (!mobile) {
    return (
      <DesktopMenuOption onClick={onClick} id={id} title={title} options={options} disabled={disabled} />
    );
  }

  return <MobileMenuOption onClick={onClick} id={id} title={title} options={options} disabled={disabled} />;
}

function DesktopMenuOption({ id, title, options, disabled = false }: DropDownMenuButtonProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        id={`dropdown-button-${id}`}
        key={`dropdown-button-${id}`}
        data-test={`dropdown-button-${id}`}
        aria-controls={open ? `dropdown-button-${id}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        disableElevation
        disableRipple
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          my: 2,
          color: "white",
          display: "flex",
          textDecoration: "none",
          backgroundColor: "transparent",
          paddingTop: 0,
          paddingBottom: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
        component={MaterialLink}
        disabled={disabled}
      >
        {title}
      </Button>
      <StyledMenu
        id={`dropdown-button-menu-${id}`}
        MenuListProps={{
          "aria-labelledby": `dropdown-button-${id}`,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        key={`dropdown-button-menu-${id}`}
        sx={{
          "& [role='menuitem'] a": {
            display: "flex",
            alignItems: "center",
            width: "100%",
            lineHeight: 1,
          },
        }}
      >
        {options.map((opt, index) => {
          return (
            <div key={`dropdown-button-menu-option-${id}-${index}`}>
              <MenuItem
                disableRipple
                disabled={opt.disabled}
                data-test={`dropdown-button-menu-option-${id}-${index}`}
              >
                <StyledLink to={opt.path} onClick={handleClose}>
                  {opt.icon}
                  {opt.title}
                </StyledLink>
              </MenuItem>
              {opt.followedByDivider && (
                <Divider key={`dropdown-button-menu-divider-${id}-${index}`} sx={{ my: 0.5 }} />
              )}
            </div>
          );
        })}
      </StyledMenu>
    </>
  );
}

function MobileMenuOption({ id, title, options, onClick, disabled = false }: DropDownMenuButtonProps) {
  const [open, setOpen] = useState(false);
  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <List component="nav" aria-labelledby={`dropdown-button-mobile-${id}`} sx={{ width: "100%", padding: 0 }}>
      <ListItemButton
        component="div"
        onClick={handleClick}
        disabled={disabled}
        disableRipple
        disableTouchRipple
      >
        <ListItemText
          primary={title}
          sx={{
            "> span": {
              textDecoration: "none",
              color: "text.primary",
              fontWeight: "bold",
              textAlign: "center",
            },
          }}
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit sx={{ backgroundColor: "#f7f7f7" }}>
        <List component="div" disablePadding sx={{ display: "inline-block", textAlign: "center" }}>
          {options.map((opt, index) => {
            return (
              <ListItemButton
                key={`dropdown-button-mobile-sublist-${id}-${index}`}
                sx={{ textAlign: "center", width: "75%", "&:hover": { background: "none" } }}
                disabled={opt.disabled}
              >
                <StyledLink
                  to={opt.path}
                  onClick={() => {
                    handleClick();
                    onClick?.();
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ lineHeight: 1 }}>
                    <Box>{opt.icon}</Box>
                    <Box>{opt.title}</Box>
                  </Stack>
                </StyledLink>
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </List>
  );
}
